import { useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SignInData, ForgotPasswordData, PasswordData } from '../../components/Forms/Auth';
import { AuthView } from './Auth.types';
import { authService, handleApiError, handleApiSuccess } from '../../services';

export const useAuthHandlers = () => {
  const [, setSearchParams] = useSearchParams();
  
  // Store temporary data between auth steps
  const tempDataRef = useRef({
    email: '',
    resetToken: '',
  });

  // Helper to navigate to a different view
  // Only update URL params, let useEffect handle state update
  const navigateToView = useCallback((view: AuthView) => {
    if (view === 'sign-in') {
      setSearchParams({});
    } else {
      setSearchParams({ view });
    }
  }, [setSearchParams]);

  const handleSignIn = useCallback(async (data: SignInData) => {
    try {
      const response = await authService.signIn({
        email: data.email,
        password: data.password,
      });

      // Log response for debugging
      console.log('Sign-in response:', response);

      // Check if response has required data
      if (!response || !response.email || !response.adminId) {
        console.error('Invalid response structure:', response);
        handleApiError(
          new Error('Invalid response from server'), 
          'The server response was invalid. Please contact support.'
        );
        throw new Error('Invalid response structure');
      }

      // Store admin data for next steps
      tempDataRef.current.email = response.email;
      
      // Check if new admin (needs to create password)
      if (response.isNewAdmin) {
        // New admin - they should receive an email with a link containing a token
        // Show a message to check their email
        handleApiSuccess('Please check your email for a link to create your password');
        navigateToView('sign-in');
      } else {
        // Existing admin - request 2FA code via email
        try {
          const twoFAResponse = await authService.request2FA(response.email);
          console.log('2FA request response:', twoFAResponse);
          handleApiSuccess('2FA code sent to your email');
          navigateToView('two-factor-auth');
        } catch (error: any) {
          // Check if error is 429 (Too Many Requests) - OTP already sent
          if (error?.response?.status === 429) {
            const errorMessage = error.response?.data?.error || 
                                error.response?.data?.message || 
                                'You already have a valid OTP. Please check your email.';
            handleApiError(error, errorMessage);
            // Still navigate to 2FA form since OTP was already sent
            navigateToView('two-factor-auth');
          } else {
            handleApiError(error, 'Failed to send 2FA code. Please try again.');
          }
        }
      }
    } catch (error: any) {
      // Handle specific error cases
      if (error?.response?.status === 404) {
        // Show backend error message for 404
        const errorMessage = error.response?.data?.message || 
                            error.response?.data?.error || 
                            'Invalid email or password. Please try again.';
        handleApiError(error, errorMessage);
        // Don't throw - just show the toast
      } else if (error?.response?.status === 401) {
        const errorMessage = error.response?.data?.message || 
                            error.response?.data?.error || 
                            'Invalid email or password. Please try again.';
        handleApiError(error, errorMessage);
        // Don't throw - just show the toast
      } else {
        handleApiError(error, 'Sign-in failed. Please check your credentials.');
      }
    }
  }, [navigateToView]);

  const handleForgotPassword = useCallback(() => {
    navigateToView('forgot-password');
  }, [navigateToView]);

  const handleForgotPasswordSubmit = useCallback(async (data: ForgotPasswordData) => {
    try {
      // Store email for next steps
      tempDataRef.current.email = data.email;
      
      console.log('Requesting OTP for forgot password with email:', data.email);
      
      // Request 2FA code (OTP) via email - same endpoint as login
      const response = await authService.request2FA(data.email);
      console.log('Forgot password - 2FA request response:', response);
      
      handleApiSuccess('Reset code sent to your email');
      navigateToView('confirm-reset-code');
    } catch (error: any) {
      console.error('Forgot password - OTP request failed:', error);
      console.error('Error status:', error?.response?.status);
      console.error('Error data:', error?.response?.data);
      
      // Check if error is 429 (Too Many Requests) - OTP already sent
      if (error?.response?.status === 429) {
        const errorMessage = error.response?.data?.error || 
                            error.response?.data?.message || 
                            'You already have a valid OTP. Please check your email.';
        handleApiError(error, errorMessage);
        // Still navigate to confirm code form since OTP was already sent
        navigateToView('confirm-reset-code');
      } else if (error?.response?.status === 404) {
        // Show backend error message for 404
        const errorMessage = error.response?.data?.message || 
                            error.response?.data?.error || 
                            'Email not found. Please check and try again.';
        handleApiError(error, errorMessage);
      } else {
        handleApiError(error, 'Failed to send reset code. Please try again.');
      }
    }
  }, [navigateToView]);

  const handleBackToSignIn = useCallback(() => {
    // Clear temporary data
    tempDataRef.current = {
      email: '',
      resetToken: '',
    };
    navigateToView('sign-in');
  }, [navigateToView]);

  const handleResetPasswordConfirmation = useCallback(async (code: string) => {
    console.log('handleResetPasswordConfirmation called with code:', code);
    console.log('Email from tempDataRef:', tempDataRef.current.email);
    
    try {
      // Verify reset code - backend returns a token in redirectUrl
      const response = await authService.verifyResetCode({
        email: tempDataRef.current.email,
        code,
      });
      console.log('Reset password - verify reset code response:', response);
      console.log('Full redirectUrl:', response.redirectUrl);
      
      // Extract token from redirectUrl query parameter
      const url = new URL(response.redirectUrl);
      const token = url.searchParams.get('token');
      
      if (token) {
        // Store the token for reset password
        tempDataRef.current.resetToken = token;
        
        handleApiSuccess(response.message || 'Password reset successfully');
        navigateToView('forgot-password-submitted');
      } else {
        handleApiError(new Error('No token in response'), 'Failed to verify code. Please try again.');
      }
    } catch (error) {
      handleApiError(error, 'Invalid or expired code. Please try again.');
    }
  }, [navigateToView]);

  const handleResetPassword = useCallback(async (data: PasswordData) => {
    try {
      // Call reset password API with token in header
      const response = await authService.resetPassword(
        {
          password: data.password,
        },
        tempDataRef.current.resetToken
      );
      console.log('Reset password response:', response);
      
      handleApiSuccess(response.message || 'Password reset successfully');
      
      // Clear temporary data
      tempDataRef.current = {
        email: '',
        resetToken: '',
      };
      
      // Navigate to sign-in page
      navigateToView('sign-in');
    } catch (error) {
      handleApiError(error, 'Failed to reset password. Please try again.');
    }
  }, [navigateToView]);

  const handleTwoFactorAuth = useCallback(async (code: string) => {
    console.log('handleTwoFactorAuth called with code:', code);
    console.log('Email from tempDataRef:', tempDataRef.current.email);
    
    try {
      // Verify 2FA code
      const response = await authService.verifyTwoFactorAuth({
        email: tempDataRef.current.email,
        code,
      });
      console.log('Two-factor auth verification response:', response);
      
      // Store authentication tokens
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);
      
      // Store user email, admin ID, and role for identifying current user
      localStorage.setItem('userEmail', response.email);
      localStorage.setItem('adminId', response.adminId);
      
      // Store role if present in response
      if (response.role) {
        localStorage.setItem('userRole', response.role);
        console.log('Stored user role:', response.role);
      }
      
      // Clear temporary data
      tempDataRef.current = {
        email: '',
        resetToken: '',
      };
      
      handleApiSuccess('Authentication successful!');
      // Use window.location for full page reload to ensure useAuth detects tokens
      window.location.href = '/admins';
    } catch (error) {
      handleApiError(error, 'Invalid 2FA code. Please try again.');
    }
  }, []);

  const handleFirstSignIn = useCallback(async (data: PasswordData) => {
    try {
      console.log('First sign in:', data);
      
      if (!data.password || !data.confirmPassword) {
        handleApiError(null, 'Please enter both password fields');
        return;
      }

      if (data.password !== data.confirmPassword) {
        handleApiError(null, 'Passwords do not match');
        return;
      }

      // Call create password API with token in header
      const response = await authService.createPassword(
        {
          password: data.password,
        },
        tempDataRef.current.resetToken
      );

      console.log('Create password response:', response);

      // Clear temporary data first
      tempDataRef.current = {
        email: '',
        resetToken: '',
      };

      handleApiSuccess('Password created successfully! Please sign in.');
      
      // Redirect to sign-in page
      navigateToView('sign-in');
    } catch (error) {
      handleApiError(error, 'Failed to create password. Please try again.');
    }
  }, [navigateToView]);

  // Check if user has valid session data for protected views
  const hasValidSession = useCallback((view: AuthView): boolean => {
    // Check URL params for token (for direct links from email)
    const urlParams = new URLSearchParams(window.location.search);
    const hasTokenInUrl = urlParams.has('token');
    
    switch (view) {
      case 'two-factor-auth':
      case 'first-sign-in':
        // These views require email from successful sign-in
        return !!(tempDataRef.current.email);
      
      case 'confirm-reset-code':
        // Allow if has token in URL (from email link) or email stored
        return hasTokenInUrl || !!tempDataRef.current.email;
      
      case 'reset-password':
        // These views require email from forgot password flow or token in URL
        return hasTokenInUrl || !!tempDataRef.current.email;
      
      case 'forgot-password-submitted':
        // This view requires email (always allow if navigating from reset flow)
        return true;
      
      case 'sign-in':
      case 'forgot-password':
        // Public views - always accessible
        return true;
      
      default:
        return true;
    }
  }, []);

  // Helper to set temp data (for URL tokens)
  const setTempData = useCallback((email: string, token?: string) => {
    tempDataRef.current.email = email;
    if (token) {
      tempDataRef.current.resetToken = token;
    }
  }, []);

  return useMemo(() => ({
    handleSignIn,
    handleForgotPassword,
    handleForgotPasswordSubmit,
    handleBackToSignIn,
    handleResetPasswordConfirmation,
    handleResetPassword,
    handleTwoFactorAuth,
    handleFirstSignIn,
    getTempEmail: () => tempDataRef.current.email,
    setTempData,
    hasValidSession,
  }), [
    handleSignIn,
    handleForgotPassword,
    handleForgotPasswordSubmit,
    handleBackToSignIn,
    handleResetPasswordConfirmation,
    handleResetPassword,
    handleTwoFactorAuth,
    handleFirstSignIn,
    setTempData,
    hasValidSession,
  ]);
};
